import { transformTimezone } from 'utils/DataTransform/utils';
import { PatrolDefInfo, TimeInfo } from 'utils/constants/informationText';
import timezones from 'static/timezones.json';
import { days, intervalOptions } from 'mocks/utils';
import API from 'services/api';
import { getItemOrDefaultToValue } from './details';

export const patrolFormFields = {
  mobilePatrol: {
    field: 'mobilePatrol',
    type: 'toggle',
    size: 'half',
    info: PatrolDefInfo.mobile,
  },
  patrolName: {
    field: 'patrolDefinitionName',
    type: 'text',
    size: 'half',
    placeholder: 'Enter patrol definition name. e.g. Evening Patrol',
    required: true,
  },
  site: ({
    options,
    required,
    disabled,
    handleSelectChange,
    size = 'half',
    ...props
  }) => ({
    field: 'site',
    type: 'select',
    size,
    options,
    required,
    disabled,
    info: PatrolDefInfo.site,
    handleSelectChange,
    ...props,
  }),
  siteSurfaceImages: ({
    options = [],
    defaultValue,
    disabled,
    info,
    handleSelectChange,
    ...props
  }) => ({
    field: 'siteSurfaceImages',
    type: 'select',
    size: 'quarter',
    disabled,
    options: [...options, defaultValue],
    info: info,
    handleSelectChange,
    ...props,
  }),
  newSite: ({ disabled, ...props } = {}) => ({
    field: 'newSite',
    type: 'toggle',
    size: 'quarter',
    info: PatrolDefInfo.newSite,
    disabled,
    ...props,
  }),
  timezone: ({ timezones, size = 'full', ...props }) => ({
    field: 'timezone',
    type: 'select',
    required: true,
    size,
    options: transformTimezone(timezones),
    info: TimeInfo.timezone,
    ...props,
  }),
  everyday: {
    field: 'everyDay',
    type: 'toggle',
    size: 'third',
    info: PatrolDefInfo.everyDay,
  },
  schedule: ({ disabled }) => ({
    field: 'schedule',
    type: 'checkbox-options',
    options: days,
    size: 'full',
    disabled,
  }),
  allDay: {
    field: 'allDay',
    type: 'toggle',
    size: 'full',
    info: PatrolDefInfo.allDay,
  },
  startTime: ({ required, disabled }) => ({
    field: 'startTime',
    label: 'Start Time',
    size: 'half',
    type: 'time',
    required,
    disabled,
    info: PatrolDefInfo.end,
  }),
  endTime: ({ required, disabled }) => ({
    field: 'endTime',
    label: 'End Time',
    size: 'half',
    type: 'time',
    required,
    disabled,
    info: PatrolDefInfo.end,
  }),
  interval: ({ options, required, disabled }) => ({
    field: 'interval',
    size: 'half',
    label: 'Interval',
    type: 'select',
    placeholder: 'How often this patrol is scheduled',
    options,
    required,
    disabled,
    info: PatrolDefInfo.interval,
  }),
  expectedDuration: {
    field: 'expectedDuration',
    fieldType: 'number',
    size: 'half',
    type: 'text',
    placeholder: 'Expected time in minutes e.g. 10',
    info: PatrolDefInfo.duration,
  },
  checkpoints: ({ additionalPolygon, additionalPolygonMode, ...props }) => ({
    field: 'checkpoints',
    label: 'Patrol Route',
    type: 'map-artist',
    requirePan: true,
    required: true,
    additionalPolygon,
    additionalPolygonMode,
    info: PatrolDefInfo.checkpoints,
    displayEvidence: true,
    ...props,
  }),
  checkpointsImage: ({
    additionalPolygon,
    additionalPolygonMode,
    selectedSiteDetailImages,
    isPatrolVirtual,
    ...props
  }) => ({
    field: 'checkpoints',
    label: 'Patrol Route',
    type: 'image-artist',
    selectedSiteDetailImages,
    requirePan: true,
    required: true,
    additionalPolygon,
    additionalPolygonMode,
    info: PatrolDefInfo.checkpoints,
    displayEvidence: true,
    isPatrolVirtual,
    ...props,
  }),
  returntostart: props => ({
    field: 'returnToStart',
    type: 'toggle',
    size: 'full',
    info: PatrolDefInfo.returnToStart,
    ...props,
  }),
  virtualPatrol: props => ({
    field: 'virtualPatrol',
    type: 'toggle',
    size: 'half',
    info: PatrolDefInfo.virtualPatrol,
    ...props,
  }),
};

export const virtualPatrolFormFields = {
  template: onChangeCallback => ({
    field: 'completeVirtualPatrol',
    label: 'Template',
    size: 'full',
    type: 'async-select',
    fetchOptions: API.fetchPatrolDefList,
    fetchFilters: { proximityAlertDisabled: true },
    onChangeCallback,
    optionLabelKey: 'definitionName',
    required: true,
  }),
  mobilePatrolIcon: mobilePatrol => ({
    field: 'mobilePatrol',
    label: 'Is Mobile',
    type: 'image-boolean',
    size: 'full',
    fieldSelection: true,
    value: mobilePatrol || false,
    disabled: true,
  }),
  patrolName: {
    field: 'patrolDefinitionName',
    type: 'text',
    size: 'half',
    placeholder: 'Enter patrol definition name. e.g. Evening Patrol',
    disabled: true,
  },
  site: {
    field: 'siteName',
    //label: 'Site Name',
    type: 'text',
    size: 'half',
    disabled: true,
  },
  scheduledStartTime: {
    field: 'startTime',
    label: 'Time Started',
    size: 'half',
    type: 'time',
    disabled: true,
    info: PatrolDefInfo.end,
  },
  businessClient: {
    field: 'businessClientName',
    label: 'Business Client Name',
    type: 'text',
    size: 'half',
    disabled: true,
  },
  expectedDuration: {
    field: 'expectedDuration',
    fieldType: 'number',
    size: 'half',
    type: 'text',
    placeholder: 'Expected time in minutes e.g. 10',
    info: PatrolDefInfo.duration,
    disabled: true,
  },
  returntostart: value => ({
    field: 'returnToStart',
    type: 'image-boolean',
    label: 'Return To Start',
    size: 'full',
    info: PatrolDefInfo.returnToStart,
    fieldSelection: true,
    value: value || false,
  }),
  checkpoints: ({
    additionalPolygon,
    additionalPolygonMode,
    isPatrolVirtual,
    ...props
  }) => ({
    field: 'checkpoints',
    label: 'Patrol route',
    type: 'map-artist',
    requirePan: true,
    required: true,
    additionalPolygon,
    additionalPolygonMode,
    info: PatrolDefInfo.checkpoints,
    displayEvidence: true,
    isPatrolVirtual,
    disableMarkerClick: true,
    ...props,
  }),
};

export const getPatrolFormField = ({
  everyDay,
  scheduledRequired,
  allDay,
  siteLocation,
}) => {
  const {
    patrolName,
    timezone,
    everyday,
    schedule,
    allDay: allDayField,
    startTime,
    endTime,
    interval,
    expectedDuration,
    checkpoints,
  } = patrolFormFields;
  return [
    patrolName,
    { ...timezone({ timezones }), field: 'patrolTimezone', size: 'half' },
    everyday,
    schedule({ disabled: everyDay }),
    allDayField,
    startTime({
      required: scheduledRequired,
      disabled: !scheduledRequired || allDay,
    }),
    endTime({
      required: scheduledRequired,
      disabled: !scheduledRequired || allDay,
    }),
    interval({
      options: intervalOptions,
      required: scheduledRequired,
      disabled: !scheduledRequired,
    }),
    expectedDuration,
    checkpoints({ additionalPolygon: siteLocation }),
  ];
};

export const getPatrolData = ({
  values,
  region,
  securityCompanyId,
  securityCompanyName,
}) => {
  const ids = values?.departments &&
    values?.departments.length > 0 && {
      departmentIds: getItemOrDefaultToValue(
        values,
        'departments',
        undefined,
      )?.map(m => m.id),
    };
  return {
    ...values,
    patrolDefinitionName: values.patrolDefinitionName,
    timezone: values.patrolTimezone,
    regionId: region.id,
    securityCompanyName,
    securityCompanyId,
    ...(ids && { departmentIds: ids.departmentIds }),
  };
};
